import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { ConditionSearchHit } from "../types";
import { useEnsureGroup } from "../hooks/useEnsureGroup";
import { usePageSetup } from "../hooks/usePageSetup";
import { useShortcuts } from "../hooks/useShortcuts";
import { selectMember, useGroupStore } from "../store/groupStore";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/Button";
import { ErrorState } from "../components/ScreenStates";
import { ConditionSearch } from "../components/condition/ConditionSearch";
import { fullName } from "../utils/format";

/**
 * One condition at a time (Brady, 2026-09-07): picking a condition goes straight to its ratings, and Add to Member returns
 * to the member. There is no basket of conditions to walk through afterwards.
 */
export function SelectConditionPage() {
  const { groupId = "", memberId = "" } = useParams();
  const navigate = useNavigate();
  const ensure = useEnsureGroup(groupId);
  const group = useGroupStore((state) => (state.group?.id === groupId ? state.group : null));
  const member = useGroupStore(selectMember(memberId));
  const memberName = member ? fullName(member.firstName, member.lastName) : "Member";
  const breadcrumb = useMemo(() => (group && member ? [{ label: "Recent Groups", to: "/uw/recent" }, { label: group.name, to: `/uw/groups/${group.id}`, status: group.status }, { label: memberName, to: `/uw/groups/${group.id}/members/${member.id}` }, { label: "Select Condition", to: "" }] : null), [group, member, memberName]);
  usePageSetup("Select Condition", breadcrumb);

  const handlePick = useCallback((hit: ConditionSearchHit) => navigate(`/uw/groups/${groupId}/members/${memberId}/conditions/${hit.conditionId}/ratings`), [navigate, groupId, memberId]);
  const handleCancel = useCallback(() => navigate(`/uw/groups/${groupId}/members/${memberId}`), [navigate, groupId, memberId]);
  const shortcuts = useMemo(() => ({ cancel: handleCancel }), [handleCancel]);
  useShortcuts(shortcuts);

  if (ensure.error) return <ErrorState message={ensure.error} onRetry={ensure.retry} />;

  return (
    <>
      <PageHeader
        title="Select Condition"
        subtitle={member ? `Add a condition to ${memberName}. Search by name, alias, or medication and pick one; its ratings come next.` : "Loading member..."}
        actions={
          <Button variant="secondary" onClick={handleCancel}>
            Back to Member
          </Button>
        }
      />
      <div className="mx-auto max-w-4xl">
        <ConditionSearch onPick={handlePick} />
      </div>
    </>
  );
}
